import { CSSInterpolation } from '@emotion/css/create-instance'
import React, { type ChangeEvent, useCallback } from 'react'

import { RadioButton, RadioButtonGroup } from './RadioButton'

type RadioButtonListOption<T extends string> = {
  /**
   * Label text shown next to this radio button.
   */
  label: string
  /**
   * The value of this radio button. This radio button is considered selected
   * when its value matches the value of its parent RadioButtonList.
   */
  value: T
  /**
   * The state of the radio button. "disabled" marks this option as disabled.
   */
  state?: 'default' | 'disabled'
}

export function DO_NOT_USE_RadioButtonListOptionJustForPropsDocs<
  T extends string
>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  props: RadioButtonListOption<T>
) {
  return null
}

// NOTE(jare): We restrict the change events that the RadioButtonList can
// fire to those with values that are in line with the passed in array of
// options. Note that it's not currently possible for a change event to be
// triggered where the value is not one of these properties. For example,
// there is no "clear" change event where the value in this event could
// be null.
type RadioButtonListChangeEvent<T extends string> = ChangeEvent<
  HTMLInputElement & { value: T }
>

/**
 * Renders a list of radio buttons, allowing a user to select a single item
 * from a list of options.
 */
export function RadioButtonList<T extends string>({
  _experimentalVariant = 'default',
  'aria-describedby': ariaDescribedBy,
  onChange,
  options,
  overrideRootStyles,
  required,
  value,
}: {
  /**
   * The value of the currently selected radio button. Must be either `null` or a value listed in the passed in `options`.
   */
  value: T | null
  /**
   * An array describing each of the radio button options that the user can choose from.
   */
  options: readonly RadioButtonListOption<T>[]
  /**
   * Called when the user selects one of the passed in `options`.
   */
  onChange: (props: { value: T; event: RadioButtonListChangeEvent<T> }) => void
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
  /**
   * Indicate whether the radio buttons are a required input
   */
  required?: boolean
  /**
   * The space-separated list of element IDs that describe the radio button list. Can be used to pass in error messages associated with this field.
   */
  'aria-describedby'?: string
  /**
   * The variant of the radio button list to render.
   *
   * `legacy` renders a specialized variant for Robinhood's web signup flow. If you need to use the
   * legacy variant elsewhere, please make a post in [#bento-design-system-discussion](https://hood.slack.com/archives/CRADQRAE7)
   * before doing so.
   */
  _experimentalVariant?: 'default' | 'legacy'
}) {
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (getIsValidRadioButtonListChangeEvent(event, options)) {
        onChange({ event, value: event.target.value })
      }
    },
    [onChange, options]
  )

  return (
    // NOTE(yan): due to different browser and screenreader behavior
    //   the error label needs to be associated both with the fieldset
    //   and with each individual radio button
    // see https://web.archive.org/web/20200623193512/https://blog.tenon.io/accessible-validation-of-checkbox-and-radiobutton-groups
    <fieldset
      aria-describedby={ariaDescribedBy}
      css={[{ border: 'none', margin: 0, padding: 0 }, overrideRootStyles]}
    >
      <RadioButtonGroup onChange={handleChange} value={value}>
        {options.map(option => (
          <RadioButton
            key={option.value}
            _experimentalVariant={_experimentalVariant}
            aria-describedby={ariaDescribedBy}
            label={option.label}
            required={required}
            state={option.state}
            value={option.value}
          />
        ))}
      </RadioButtonGroup>
    </fieldset>
  )
}

function getIsValidRadioButtonListChangeEvent<T extends string>(
  e: ChangeEvent<HTMLInputElement>,
  options: readonly RadioButtonListOption<T>[]
): e is RadioButtonListChangeEvent<T> {
  return options.some(option => option.value === e.target.value)
}
